import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { getApiBase, getToken } from '../../lib/utils'

type Agent = {
  id: number
  name: string
  subtitle?: string | null
}

export default function IFramePage() {
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-orange-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(249,115,22,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-6">
          <Link
            to="/integrations"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour aux intégrations
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Script embarqué</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">Intégrez Totemium dans n'importe quel site</p>
        </div>

        <article className="relative overflow-hidden rounded-3xl border border-zinc-200/80 bg-white/95 p-6 shadow-md backdrop-blur dark:border-zinc-800/70 dark:bg-zinc-900/85">
          <div aria-hidden className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-orange-400/15 blur-3xl dark:bg-orange-500/20" />
          <div className="relative space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Script embarqué & CMS custom</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">Injectez un simple snippet JavaScript pour intégrer Totemium</p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition hover:bg-orange-600"
              >
                Obtenir le code
              </button>
            </div>

            <div className="rounded-2xl border border-sky-200/70 bg-sky-50/60 px-4 py-3 text-sm dark:border-sky-800 dark:bg-sky-900/20">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-100">
                Bonnes pratiques
              </h3>
              <ul className="space-y-2 text-xs text-sky-800 dark:text-sky-200 sm:text-sm">
                <li>• Placez le script juste avant <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">&lt;/head&gt;</code> pour charger le widget rapidement</li>
                <li>• <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">data-agent-id</code> cible un agent particulier Totemium</li>
                <li>• Ajoutez un trigger (bouton flottant) si vous désactivez l'auto-open</li>
              </ul>
            </div>

            <div className="space-y-4 text-sm text-zinc-600 dark:text-zinc-300">
              <p>
                Vous pouvez intégrer Totemium dans n'importe quel site en copiant le snippet JavaScript généré automatiquement.
              </p>
            </div>
          </div>
        </article>
      </div>

      {drawerOpen && <ScriptDrawer onClose={() => setDrawerOpen(false)} />}
    </div>
  )
}

type ScriptDrawerProps = {
  onClose?: () => void
}

function ScriptDrawer({ onClose }: ScriptDrawerProps = {}) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  const [autoOpen, setAutoOpen] = useState(true)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const token = useMemo(() => getToken(), [])

  useEffect(() => {
    let mounted = true
    setAgentsLoading(true)
    ;(async () => {
      try {
        const { data } = await api.get('/agents')
        if (!mounted) return
        const items: Agent[] = Array.isArray(data?.items)
          ? data.items.map((item: any) => ({ id: item.id, name: item.name, subtitle: item.subtitle }))
          : []
        setAgents(items)
        if (items.length && !selectedAgentId) {
          setSelectedAgentId(String(items[0].id))
        }
      } catch {
        if (mounted) setAgents([])
      } finally {
        if (mounted) setAgentsLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [selectedAgentId])

  const apiBase = useMemo(() => {
    if (typeof window === 'undefined') return getApiBase()
    return `${window.location.origin}${getApiBase()}`
  }, [])

  const agentOptions = useMemo(
    () => agents.map(agent => ({ value: String(agent.id), label: agent.name })),
    [agents]
  )

  const embedSrc = useMemo(() => {
    if (typeof window === 'undefined') return `${getApiBase()}/embed/embed.js`
    return `${window.location.origin}/api/embed/embed.js`
  }, [])

  const snippet = useMemo(() => {
    const agentAttr = selectedAgentId ? `\n        data-agent-id="${selectedAgentId}"` : ''
    const tokenAttr = token ? `\n        data-token="${token}"` : ''
    const autoOpenAttr = `\n        data-auto-open="${autoOpen ? 'true' : 'false'}"`
    return `<script data-totemium="embed" src="${embedSrc}"\n        data-api-base="${apiBase}"${tokenAttr}${agentAttr}${autoOpenAttr} defer></script>`
  }, [apiBase, autoOpen, embedSrc, selectedAgentId, token])

  const fullHtml = useMemo(() => {
    return `<!doctype html>\n<html lang="fr">\n  <head>\n    <meta charset="utf-8" />\n    <meta name="viewport" content="width=device-width, initial-scale=1" />\n    <title>Totemium – Intégration</title>\n    ${snippet}\n  </head>\n  <body style="font-family: system-ui, sans-serif; background: #f9fafb; color: #111827;">\n    <main style="max-width: 640px; margin: 80px auto; padding: 0 24px; text-align: center;">\n      <h1 style="font-size: 2rem; font-weight: 700; margin-bottom: 16px;">Widget Totemium</h1>\n      <p style="margin-bottom: 24px;">Cette page minimale embarque le widget Totemium grâce au script ci-dessus.</p>\n      <p>Vous pouvez personnaliser le style de votre page selon vos besoins ; le widget Totemium se charge automatiquement.</p>\n    </main>\n  </body>\n</html>`
  }, [snippet])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2500)
    } catch (error) {
      console.error('Clipboard error', error)
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2500)
    }
  }, [snippet])

  return (
    <div className="fixed inset-0 z-[120] flex items-stretch justify-end bg-zinc-900/40 backdrop-blur-sm">
      <button
        type="button"
        className="flex-1"
        aria-label="Fermer"
        onClick={onClose}
      />
      <div className="relative h-full w-full max-w-[min(600px,50vw)] overflow-y-auto border-l border-orange-500/20 bg-white shadow-xl dark:bg-zinc-950">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4 dark:border-zinc-800">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Snippet embarqué Totemium</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">Générez dynamiquement le script à intégrer sur votre site.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-900"
            aria-label="Fermer le panneau"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <section className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Agent connecté</label>
              {agentsLoading ? (
                <p className="mt-2 text-sm text-zinc-500">Chargement des agents…</p>
              ) : agentOptions.length ? (
                <select
                  value={selectedAgentId}
                  onChange={(event) => setSelectedAgentId(event.target.value)}
                  className="mt-2 w-full rounded border border-zinc-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-zinc-700"
                >
                  {agentOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <p className="mt-2 text-sm text-zinc-500">
                  Aucun agent disponible. Créez un agent dans Totemium pour générer un snippet personnalisé.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <input
                id="auto-open"
                type="checkbox"
                checked={autoOpen}
                onChange={(event) => setAutoOpen(event.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500"
              />
              <label htmlFor="auto-open" className="text-sm text-zinc-600 dark:text-zinc-300">
                Ouvrir automatiquement le widget au chargement
              </label>
            </div>

            <div className="rounded-2xl border border-zinc-200/70 bg-zinc-950/95 p-4 text-xs text-zinc-50 shadow-inner dark:border-zinc-700/60">
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap">{snippet}</pre>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Version HTML complète</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Copiez cette page minimale si vous souhaitez tester l'intégration hors CMS.
              </p>
              <div className="rounded-2xl border border-zinc-200/70 bg-zinc-950/95 p-4 text-xs text-zinc-50 shadow-inner dark:border-zinc-700/60">
                <pre className="max-h-72 overflow-auto whitespace-pre-wrap">{fullHtml}</pre>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/40 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!token}
              >
                Copier le code
              </button>
              {!token && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Connectez-vous pour récupérer votre token d'accès.
                </span>
              )}
              {copyState === 'copied' && (
                <span className="text-xs text-orange-600 dark:text-orange-300">Code copié !</span>
              )}
              {copyState === 'error' && (
                <span className="text-xs text-rose-600 dark:text-rose-400">Impossible de copier automatiquement.</span>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
